import {
	Box,
	Button,
	Chip,
	List,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Send as SendIcon } from "@material-ui/icons";
import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from "@microsoft/signalr";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChatMessage, ConnectionState } from "./types";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		maxWidth: 800,
		margin: "0 auto",
		padding: theme.spacing(3),
	},
	messageList: {
		height: 400,
		overflow: "auto",
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.default,
	},
	messageForm: {
		display: "flex",
		gap: theme.spacing(2),
	},
	messageInput: {
		flexGrow: 1,
	},
	timestamp: {
		color: theme.palette.text.secondary,
		fontSize: "0.8rem",
	},
	statusChip: {
		marginBottom: theme.spacing(2),
	},
}));

const ChatComponent: React.FC = () => {
	const classes = useStyles();
	const [hubUrl, setHubUrl] = useState<string>("/ws");
	const [connection, setConnection] = useState<HubConnection | null>(null);
	const [connectionState, setConnectionState] = useState<ConnectionState>({
		isConnected: false,
	});
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [user, setUser] = useState<string>("");
	const [message, setMessage] = useState<string>("");
	const messageEndRef = useRef<null | HTMLDivElement>(null);

	const latestMessages = useRef<ChatMessage[]>([]);
	latestMessages.current = messages;

	const scrollToBottom = () => {
		messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const startConnection = useCallback(async () => {
		try {
			const newConnection = new HubConnectionBuilder()
				.withUrl(hubUrl)
				.withAutomaticReconnect()
				.configureLogging(LogLevel.Information)
				.build();

			newConnection.on("ReceiveMessage", (user: string, message: string) => {
				const newMessage: ChatMessage = {
					user,
					message,
					timestamp: new Date(),
				};
				setMessages((prev) => [...prev, newMessage]);
			});

			newConnection.on("UserConnected", (connectionId: string) => {
				console.log(`User connected: ${connectionId}`);
				setConnectionState((prev) => ({
					...prev,
					isConnected: true,
					connectionId,
				}));
			});

			newConnection.on("UserDisconnected", (connectionId: string) => {
				console.log(`User disconnected: ${connectionId}`);
				if (connectionId === connectionState.connectionId) {
					setConnectionState((prev) => ({
						...prev,
						isConnected: false,
						connectionId: undefined,
					}));
				}
			});

			await newConnection.start();
			setConnection(newConnection);
			console.log("SignalR Connected");
		} catch (err) {
			console.error("SignalR Connection Error:", err);
			setConnectionState((prev) => ({
				...prev,
				isConnected: false,
				error: err as Error,
			}));
		}
	}, [hubUrl]);

	useEffect(() => {
		startConnection();
		return () => {
			connection?.stop();
		};
	}, [startConnection]);

	const sendMessage = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!connection || !message || !user) return;

		try {
			await connection.invoke("SendMessage", user, message);
			setMessage("");
		} catch (err) {
			console.error("Error sending message:", err);
		}
	};

	return (
		<Paper className={classes.root} elevation={3}>
			<Box mb={2}>
				<Chip
					label={connectionState.isConnected ? "Connected" : "Disconnected"}
					color={connectionState.isConnected ? "primary" : "default"}
					className={classes.statusChip}
				/>
				{connectionState.error && (
					<Typography color="error">
						Error: {connectionState.error.message}
					</Typography>
				)}
			</Box>

			<Paper className={classes.messageList} variant="outlined">
				<List>
					{messages.map((msg, index) => (
						<ListItem key={index}>
							<ListItemText
								primary={
									<Typography>
										<strong>{msg.user}:</strong> {msg.message}
									</Typography>
								}
								secondary={
									<span className={classes.timestamp}>
										{msg.timestamp.toLocaleTimeString()}
									</span>
								}
							/>
						</ListItem>
					))}
					<div ref={messageEndRef} />
				</List>
			</Paper>

			<form onSubmit={sendMessage} className={classes.messageForm}>
				<TextField
					value={user}
					onChange={(e) => setUser(e.target.value)}
					label="Your name"
					variant="outlined"
					size="small"
				/>
				<TextField
					className={classes.messageInput}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					label="Type a message"
					variant="outlined"
					size="small"
				/>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={!connectionState.isConnected || !user || !message}
					endIcon={<SendIcon />}
				>
					Send
				</Button>
			</form>
		</Paper>
	);
};

export default ChatComponent;
